import { useEffect, useState } from 'react'
import paymentService from '../utils/paymentService'

interface FormPaymentResult {
  isPaid: boolean
  isChecking: boolean
  subscription: any | null
  checkPayment: (formId: string) => Promise<boolean>
  showPaymentModal: boolean
  setShowPaymentModal: (show: boolean) => void
  currentFormId: string | null
}

/**
 * Hook to check and manage payment status for specific forms (CT600 or Self Assessment)
 * Based on the payment integration guide (formid.md)
 * 
 * Usage:
 * - Call checkPayment(formId) before allowing form submission or completion
 * - If isPaid is false, show payment modal with the formId
 * - After payment, verify again using checkPayment
 */
export const useFormPayment = (): FormPaymentResult => {
  const [isPaid, setIsPaid] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [subscription, setSubscription] = useState<any | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [currentFormId, setCurrentFormId] = useState<string | null>(null)

  /**
   * Check if a specific form has been paid for
   * @param formId - The MongoDB ObjectId of the form (_id from CT600Form or SelfAssessment)
   * @returns true if paid, false otherwise
   */
  const checkPayment = async (formId: string): Promise<boolean> => {
    if (!formId) {
      console.warn('No formId provided for payment check')
      return false
    }

    try {
      setIsChecking(true)
      setCurrentFormId(formId)
      
      // Call GET /payments/subscription/:formId
      const result = await paymentService.checkFormPayment(formId)
      
      if (result.success && result.data) {
        const status = result.data.status
        const planSlug = result.data.planSlug
        
        // Payment is valid if status is 'paid' or 'completed'
        const paid = status === 'paid' || status === 'completed'
        
        setIsPaid(paid)
        setSubscription(result.data)
        
        console.log(`Payment check for form ${formId}: ${paid ? 'PAID' : 'UNPAID'}`, {
          status,
          planSlug
        })
        
        return paid
      }
      
      // No subscription found (404) or unsuccessful response
      setIsPaid(false)
      setSubscription(null)
      return false
      
    } catch (error: any) {
      console.error(`Payment check failed for form ${formId}:`, error)
      
      // 404 means no payment exists for this form
      if (error?.status === 404) {
        setIsPaid(false)
        setSubscription(null)
        return false
      }
      
      // For other errors, assume unpaid to be safe
      setIsPaid(false)
      setSubscription(null)
      return false
    } finally {
      setIsChecking(false)
    }
  }

  return {
    isPaid,
    isChecking,
    subscription,
    checkPayment,
    showPaymentModal,
    setShowPaymentModal,
    currentFormId,
  }
}

/**
 * Hook that automatically checks payment on mount for a given formId
 * Useful when you already have a formId and want to check immediately
 */
export const useFormPaymentCheck = (formId: string | null) => {
  const payment = useFormPayment()

  useEffect(() => {
    if (formId) {
      payment.checkPayment(formId)
    }
  }, [formId])

  return payment
}
